import type { OptionValue, Step, Substep } from "@views/Upholstery/types"
import {
  ROW_COUNT_OPTIONS,
  ROW_COUNT_SUBSTEP_KEY,
} from "../../constants/row-count-choice"
import type { InferredRowLayout } from "../../pdm/compareSeatingRowToInferred"
import type { PdmAssetRecord } from "../../types"
import type { PdmFlatRow } from "../../pdm/normalizePdm"
import { getSeatAndRowSlices, unwrapValue } from "../../pdm/normalizePdm"
import {
  getPdmStepStructure,
  isPdmSourcedStep,
} from "../../constants/step-structure"
import {
  getDatoSubstepOptionValues,
  overlayDatoSkusOnPdmOptions,
  filterDatoPdmAssetsByPdmValues,
} from "./pdm-option-sku"
import {
  buildRowOptionValues,
  makeOptionFactories,
  mapLabelsToOptions,
} from "./pdm-option-factories"
import {
  attachTrimCompatibilityToBackOptions,
  attachTrimCompatibilityToMiddleOptions,
  attachTrimCompatibilityToOptions,
} from "./trimCompatibleOptions"
import type { FactoryMatch } from "@views/Upholstery/types"
import type { FactoryMatchFilterConfig } from "../factory/factory-match-filter"

export type ExtractedBodyRowOptions = {
  trimOpts: OptionValue[]
  bodyTypeOpts: OptionValue[]
  drvOpts: OptionValue[]
  pasOpts: OptionValue[]
  frontOpts: OptionValue[]
  backOpts: OptionValue[]
  midOpts: OptionValue[]
  fronts: string[]
  backs: string[]
  middles: string[]
  rawFrontAttributes: Array<{
    attribute: string
    attribute_value: string
    record_number?: number
    item_part_attribute_id?: number
    tags?: unknown[]
  }>
  rawBackAttributes: Array<{
    attribute: string
    attribute_value: string
    record_number?: number
    item_part_attribute_id?: number
    tags?: unknown[]
  }>
  rawMiddleAttributes: Array<{
    attribute: string
    attribute_value: string
    record_number?: number
    item_part_attribute_id?: number
    tags?: unknown[]
  }>
}

/** Collect Body + Rows option labels from flattened PDM rows. */
export function extractBodyAndRowOptionsFromFlat(
  flat: PdmFlatRow[]
): ExtractedBodyRowOptions {
  const { mk } = makeOptionFactories()

  const trims = flat.map((r) =>
    unwrapValue(r.application.sub_model ?? r.application.SubModel)
  )
  const bodies = flat.map((r) =>
    unwrapValue(
      r.application.body_type ??
        r.application.bodyType ??
        r.application.BodyType
    )
  )

  const driverSeats = flat
    .map((r) => getSeatAndRowSlices(r.product).driverSeat)
    .filter(Boolean)
  const passengerSeats = flat
    .map((r) => getSeatAndRowSlices(r.product).passengerSeat)
    .filter(Boolean)
  const fronts = flat
    .map((r) => getSeatAndRowSlices(r.product).frontRow)
    .filter(Boolean)
  const backs = flat
    .map((r) => getSeatAndRowSlices(r.product).backRow)
    .filter(Boolean)
  const middles = flat
    .map((r) => getSeatAndRowSlices(r.product).middleRow)
    .filter(Boolean)

  const rawFrontAttributes = flat
    .map((r) => {
      const attrs = r.product.part_attributes as Array<Record<string, unknown>>
      return attrs?.find((a) => a.attribute === "Front Row")
    })
    .filter(Boolean) as ExtractedBodyRowOptions["rawFrontAttributes"]

  const rawBackAttributes = flat
    .map((r) => {
      const attrs = r.product.part_attributes as Array<Record<string, unknown>>
      return attrs?.find((a) => a.attribute === "Back Row")
    })
    .filter(Boolean) as ExtractedBodyRowOptions["rawBackAttributes"]

  const rawMiddleAttributes = flat
    .map((r) => {
      const attrs = r.product.part_attributes as Array<Record<string, unknown>>
      return attrs?.find((a) => a.attribute === "Middle Row")
    })
    .filter(Boolean) as ExtractedBodyRowOptions["rawMiddleAttributes"]

  const trimOpts = mapLabelsToOptions(trims, mk, "trim")
  // Body types come from all PDM rows, but not every trim uses every body type.
  // Example: F-150 XL has Extended/Standard cab in PDM, not Crew Cab.
  // Tag each body type with the trims that appear with it so the UI can hide the rest.
  const bodyTypeOpts = attachTrimCompatibilityToOptions(
    flat,
    mapLabelsToOptions(bodies, mk, "body-step"),
    (row) =>
      unwrapValue(
        row.application.body_type ??
          row.application.bodyType ??
          row.application.BodyType ??
          ""
      )
  )
  const drvOpts = attachTrimCompatibilityToOptions(
    flat,
    mapLabelsToOptions(driverSeats, mk, "driver-seat-type"),
    (row) => getSeatAndRowSlices(row.product).driverSeat
  )
  const pasOpts = attachTrimCompatibilityToOptions(
    flat,
    mapLabelsToOptions(passengerSeats, mk, "passenger-seat-type"),
    (row) => getSeatAndRowSlices(row.product).passengerSeat
  )
  const frontOpts = mapLabelsToOptions(fronts, mk, "front")
  // Same idea as body type: only show back-row layouts that PDM lists for the trim.
  const backOpts = attachTrimCompatibilityToBackOptions(
    flat,
    mapLabelsToOptions(backs, mk, "back", { splitCommas: false })
  )
  const midOptsRaw = middles.some((x) => x.trim() !== "")
    ? mapLabelsToOptions(middles, mk, "middle", { splitCommas: false })
    : []
  // Same idea as body type: only show middle-row layouts that PDM lists for the trim.
  // Example: Z71 → only 50-50.
  const midOpts = attachTrimCompatibilityToMiddleOptions(flat, midOptsRaw)

  return {
    trimOpts,
    bodyTypeOpts,
    drvOpts,
    pasOpts,
    frontOpts,
    backOpts,
    midOpts,
    fronts,
    backs,
    middles,
    rawFrontAttributes,
    rawBackAttributes,
    rawMiddleAttributes,
  }
}

export function buildBodyStepFromOptions(
  opts: ExtractedBodyRowOptions,
  datoBodyStep: Step | undefined
): Step {
  const bodyStep = getPdmStepStructure("body")
  bodyStep.substeps = bodyStep.substeps.map((ss) => {
    let optionValues: OptionValue[] = []
    switch (ss.key) {
      case "trim":
        optionValues = overlayDatoSkusOnPdmOptions(
          opts.trimOpts,
          getDatoSubstepOptionValues(datoBodyStep, "trim")
        )
        break
      case "body-step":
        optionValues = overlayDatoSkusOnPdmOptions(
          opts.bodyTypeOpts,
          getDatoSubstepOptionValues(datoBodyStep, "body-step")
        )
        break
      case "driver-seat-type":
        optionValues = overlayDatoSkusOnPdmOptions(
          opts.drvOpts,
          getDatoSubstepOptionValues(datoBodyStep, "driver-seat-type")
        )
        break
      case "passenger-seat-type":
        optionValues = overlayDatoSkusOnPdmOptions(
          opts.pasOpts,
          getDatoSubstepOptionValues(datoBodyStep, "passenger-seat-type")
        )
        break
    }
    return {
      ...ss,
      category: {
        ...ss.category!,
        optionValues,
      },
    }
  })
  return bodyStep
}

export function buildRowsStepFromOptions(
  opts: ExtractedBodyRowOptions,
  datoRowsStep: Step | undefined,
  datoPdmAssetsFiltered: PdmAssetRecord[] | null,
  rowCountChoiceDefault: InferredRowLayout | null = null
): Step {
  const rowsStep = getPdmStepStructure("rows")
  const rowsSubsteps: Substep[] = []

  if (rowCountChoiceDefault) {
    const preferred = ROW_COUNT_OPTIONS.find(
      (option) => option.optionValue === rowCountChoiceDefault
    )!
    const other = ROW_COUNT_OPTIONS.find(
      (option) => option.optionValue !== rowCountChoiceDefault
    )!
    rowsSubsteps.push({
      id: "pdm-rows-row-count",
      key: ROW_COUNT_SUBSTEP_KEY,
      title: "How Many Rows",
      defaultValue: undefined,
      category: {
        optionName: "How Many Rows",
        // PDM answer first so existing default logic preselects it.
        optionValues: [{ ...preferred }, { ...other }],
      },
    })
  }

  rowsSubsteps.push({
    ...rowsStep.substeps.find((ss) => ss.key === "front")!,
    category: {
      optionName: "Front Row",
      optionValues: buildRowOptionValues(
        opts.frontOpts,
        datoRowsStep,
        "front",
        "Front Row",
        datoPdmAssetsFiltered
      ),
    },
  })

  if (opts.midOpts.length > 0) {
    rowsSubsteps.push({
      ...rowsStep.substeps.find((ss) => ss.key === "middle")!,
      category: {
        optionName: "Middle Row",
        optionValues: buildRowOptionValues(
          opts.midOpts,
          datoRowsStep,
          "middle",
          "Middle Row",
          datoPdmAssetsFiltered
        ),
      },
    })
  }

  rowsSubsteps.push({
    ...rowsStep.substeps.find((ss) => ss.key === "back")!,
    category: {
      optionName: "Back Row",
      optionValues: buildRowOptionValues(
        opts.backOpts,
        datoRowsStep,
        "back",
        "Back Row",
        datoPdmAssetsFiltered
      ),
    },
  })

  rowsStep.substeps = rowsSubsteps
  return rowsStep
}

export function assembleMergedSteps(args: {
  bodyStep: Step
  rowsStep: Step
  factoryMatchStep: Step
  datoSteps: Step[]
}): Step[] {
  const result: Step[] = []
  result.push(args.bodyStep)
  result.push(args.rowsStep)

  const nonPdmDatoSteps = args.datoSteps.filter(
    (step) => !isPdmSourcedStep(step.key)
  )
  for (const datoStep of nonPdmDatoSteps) {
    result.push(datoStep)
  }

  result.push(args.factoryMatchStep)
  return result
}

export function filterRowAssetsForOptions(
  datoPdmAssetsRaw: PdmAssetRecord[] | null,
  opts: ExtractedBodyRowOptions
): PdmAssetRecord[] | null {
  if (!datoPdmAssetsRaw) return null
  return filterDatoPdmAssetsByPdmValues(datoPdmAssetsRaw, {
    front: opts.frontOpts,
    back: opts.backOpts,
    middle: opts.midOpts,
  })
}

export function buildFactoryMatchStepShell(
  factoryMatches: FactoryMatch[],
  factoryMatchFilter: FactoryMatchFilterConfig
): Step {
  const factoryMatchStep = getPdmStepStructure("factory-match")
  factoryMatchStep.factoryMatches = factoryMatches
  factoryMatchStep.factoryMatchFilter = factoryMatchFilter
  return factoryMatchStep
}
